import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, Lock, CreditCard } from '@phosphor-icons/react';

interface PlanDetails {
  name: string;
  price: number;
  billing: 'monthly' | 'yearly';
  features: string[];
}

const plans: Record<string, PlanDetails> = {
  'professional-monthly': {
    name: 'Professional',
    price: 49,
    billing: 'monthly',
    features: [
      'Unlimited public dataset analyses',
      'Private data upload (up to 500 samples)',
      'All deconvolution algorithms',
      'Priority processing queue',
      'Advanced visualizations & exports',
      'API access for automation',
      '90-day data retention',
      'Email support'
    ]
  },
  'professional-yearly': {
    name: 'Professional',
    price: 490,
    billing: 'yearly',
    features: [
      'Unlimited public dataset analyses',
      'Private data upload (up to 500 samples)',
      'All deconvolution algorithms',
      'Priority processing queue',
      'Advanced visualizations & exports',
      'API access for automation',
      '90-day data retention',
      'Email support',
      'Save 17% with annual billing'
    ]
  }
};

export function Checkout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  // Pinned on mount: reading the clock during render makes the date drift
  // between renders and is flagged by react-hooks/purity.
  const [firstChargeDate] = useState(() => new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));
  const [couponError, setCouponError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const planKey = `professional-${billingCycle}`;
  const plan = plans[planKey];

  const subtotal = plan.price;
  const discount = subtotal * (appliedDiscount / 100);
  const total = subtotal - discount;

  const handleApplyCoupon = () => {
    if (couponCode.toUpperCase() === 'LAUNCH20') {
      setAppliedDiscount(20);
      setCouponError('');
    } else if (couponCode.toUpperCase() === 'RESEARCH10') {
      setAppliedDiscount(10);
      setCouponError('');
    } else {
      setCouponError('That code is not valid.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    // Mock payment processing
    setTimeout(() => {
      setIsProcessing(false);
      navigate('/checkout/success');
    }, 2000);
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  const formatExpiry = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + (v.length > 2 ? '/' + v.substring(2, 4) : '');
    }
    return v;
  };

  if (!user) return null;

  return (
    <div className="min-h-screen pt-24 pb-12 bg-background">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-2 transition-colors text-sm"
          >
            ← Back
          </button>
          <h1 className="text-foreground mb-2">Complete your subscription</h1>
          <p className="text-muted-foreground">
            Start your 14-day free trial of CellReveal Professional
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Payment Form */}
          <div className="lg:col-span-2">
            <Card className="p-8 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-teal-700" />
                </div>
                <h3>Payment Information</h3>
              </div>

              {/* Billing Cycle Toggle */}
              <div className="mb-8 p-4 bg-muted rounded-lg">
                <label className="block text-foreground mb-3">Billing Cycle</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setBillingCycle('monthly')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      billingCycle === 'monthly'
                        ? 'border-teal-600 bg-background'
                        : 'border-border bg-background'
                    }`}
                  >
                    <p className="text-foreground mb-1">Monthly</p>
                    <p className="text-muted-foreground">$49/month</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('yearly')}
                    className={`p-4 rounded-lg border-2 transition-all relative ${
                      billingCycle === 'yearly'
                        ? 'border-teal-600 bg-background'
                        : 'border-border bg-background'
                    }`}
                  >
                    <div className="absolute -top-3 right-2">
                      <Badge variant="default">Save 17%</Badge>
                    </div>
                    <p className="text-foreground mb-1">Yearly</p>
                    <p className="text-muted-foreground">$490/year</p>
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Card Number */}
                <div className="mb-6">
                  <label className="block text-foreground mb-2">
                    Card Number
                  </label>
                  <input
                    type="text"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                    required
                    className="w-full px-4 py-3 border border-input rounded-lg focus:outline-hidden focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  {/* Expiry */}
                  <div>
                    <label className="block text-foreground mb-2">
                      Expiry Date
                    </label>
                    <input
                      type="text"
                      value={expiry}
                      onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                      placeholder="MM/YY"
                      maxLength={5}
                      required
                      className="w-full px-4 py-3 border border-input rounded-lg focus:outline-hidden focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>

                  {/* CVC */}
                  <div>
                    <label className="block text-foreground mb-2">
                      CVC
                    </label>
                    <input
                      type="text"
                      value={cvc}
                      onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').substring(0, 4))}
                      placeholder="123"
                      maxLength={4}
                      required
                      className="w-full px-4 py-3 border border-input rounded-lg focus:outline-hidden focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Coupon Code */}
                <div className="mb-6">
                  <label className="block text-foreground mb-2">
                    Coupon Code <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-1 px-4 py-3 border border-input rounded-lg focus:outline-hidden focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleApplyCoupon}
                    >
                      Apply
                    </Button>
                  </div>
                  {couponError && (
                    <p className="text-destructive text-sm mt-2">{couponError}</p>
                  )}
                  {appliedDiscount > 0 && (
                    <p className="text-teal-600 mt-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {appliedDiscount}% discount applied
                    </p>
                  )}
                  <p className="text-muted-foreground mt-2">
                    Try: <code className="bg-muted px-2 py-1 rounded text-foreground">LAUNCH20</code> or <code className="bg-muted px-2 py-1 rounded text-foreground">RESEARCH10</code>
                  </p>
                </div>

                {/* Security Notice */}
                <div className="p-4 border border-border rounded-lg mb-6">
                  <div className="flex items-start gap-3">
                    <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-foreground font-medium mb-1">Secure payment processing</p>
                      <p className="text-muted-foreground text-sm">
                        Payment information is encrypted and processed securely through Stripe. Card details are never stored.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="default"
                  className="w-full"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    `Start Free Trial`
                  )}
                </Button>

                <p className="text-muted-foreground text-center mt-4">
                  You won't be charged until your 14-day trial ends
                </p>
              </form>
            </Card>

            {/* Trial Info */}
            <div className="p-5 border border-border rounded-lg">
              <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-3">
                Your free trial
              </p>
              <div className="divide-y divide-border">
                {[
                  'Full access to all Professional features for 14 days',
                  'No commitment — cancel anytime before the trial ends',
                  'Reminder email sent before your trial expires',
                  'One-click cancellation in account settings',
                ].map(item => (
                  <p key={item} className="py-2 text-sm text-muted-foreground">{item}</p>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-6">
              <h3 className="mb-6">Order Summary</h3>

              <div className="mb-6 pb-6 border-b border-border">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-foreground mb-1">{plan.name}</h4>
                    <p className="text-muted-foreground">
                      {billingCycle === 'monthly' ? 'Monthly' : 'Annual'} billing
                    </p>
                  </div>
                  <Badge variant="default">14-day trial</Badge>
                </div>

                <div className="space-y-2 text-foreground">
                  {plan.features.slice(0, 5).map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                  <p className="text-muted-foreground italic">
                    + {plan.features.length - 5} more features
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-foreground">${subtotal}</span>
                </div>
                {appliedDiscount > 0 && (
                  <div className="flex items-center justify-between text-teal-600">
                    <span>Discount ({appliedDiscount}%)</span>
                    <span>-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="pt-3 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-foreground">
                      <strong>Total</strong>
                    </span>
                    <div className="text-right">
                      <p className="text-foreground">
                        <strong>${total.toFixed(2)}</strong>
                      </p>
                      <p className="text-muted-foreground">
                        per {billingCycle === 'monthly' ? 'month' : 'year'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 border border-border rounded-lg">
                <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-1">
                  First charge
                </p>
                <p className="text-sm text-foreground">
                  {firstChargeDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
